/**
 * Cross-module signal for "the dashboard session is no longer valid".
 *
 * Two API clients can decide a session is dead:
 *   - `lib/api.ts`     — dashboard server returned 401 and the refresh
 *                        also failed.
 *   - `crm/lib/api.ts` — brain crm-api returned 401.
 *
 * Both call `notifySessionLost()`. The AuthProvider listens once at
 * mount and reacts by clearing `tokenStore`, dropping `user` to null,
 * and flipping status to "ready" — which causes every `<RequireAuth>`
 * gate to bounce to /login on the next render.
 *
 * This is what the previous "throw an ApiError and hope the component
 * handles it" flow was missing: there was no global signal, so a
 * deeply-nested component could fail silently while the chrome above
 * it kept rendering with a stale `user`.
 *
 * The event is a plain CustomEvent on `window` — works inside the SPA
 * and inside the Tauri webview without a separate transport.
 */

import { tokenStore } from "./tokenStore.js";

export const SESSION_LOST_EVENT = "xop:session-lost";

export interface SessionLostDetail {
  /** Where the signal originated, for telemetry / log analysis. */
  source: "dashboard-api" | "crm-api" | "auth-provider";
  /** HTTP status that triggered the signal, if any. */
  status?: number;
}

/**
 * Fire the event AND eagerly clear stored tokens. Eager clearing keeps
 * the failure mode safe even if no listener is registered yet (or the
 * listener crashes before clearing): the next /me round-trip will hit
 * the unauthenticated branch and bounce to /login.
 */
export function notifySessionLost(detail: SessionLostDetail): void {
  if (typeof window === "undefined") return;
  // Clear first so a listener that re-checks tokens sees the post-state.
  tokenStore.clear();
  window.dispatchEvent(
    new CustomEvent<SessionLostDetail>(SESSION_LOST_EVENT, { detail }),
  );
}

/**
 * Subscribe to session-lost events. Returns an `off()` cleanup the
 * caller wires into a `useEffect` return value.
 */
export function onSessionLost(
  handler: (detail: SessionLostDetail) => void,
): () => void {
  if (typeof window === "undefined") return () => {};
  const listener = (e: Event): void => {
    handler((e as CustomEvent<SessionLostDetail>).detail);
  };
  window.addEventListener(SESSION_LOST_EVENT, listener);
  return () => window.removeEventListener(SESSION_LOST_EVENT, listener);
}
